import { cx } from "@emotion/css";
import Skeleton from "@mui/material/Skeleton";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OutlinedPrimaryButton } from "src/components/Default/LekUI/Button";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import CourseView from "src/components/NewComponents/Views/ScoreViews/CourseView";
import { useGetFolder, useGetFolderItemsInfinite } from "src/hooks/useFolders";
import { FolderEmptyItem } from "./FolderEmptyItem";
import { FolderEmptyListView } from "./FolderEmptyListView";
import { FolderSelectionCheckbox } from "./FolderSelectionCheckbox";
import type { FolderItemsProps } from "./types";

export const FolderCoursesItems = ({
	folderId,
	searchValue,
	isSelectMode,
	selectedItems,
	toggleSelection,
}: FolderItemsProps) => {
	const { t } = useTranslation();
	const { openModal } = useModals();

	const { data: folder } = useGetFolder(folderId);

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useGetFolderItemsInfinite(
			folderId,
			{
				take: 10,
				type: "course",
				...(searchValue ? { name: searchValue } : {}),
			},
			!!folderId,
		);

	const flatItems = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flatMap((page: any) => page.data || []);
	}, [data?.pages]);

	const totalCount = data?.pages?.[0]?.totalCount || 0;

	if (isLoading) {
		return (
			<div className="flex flex-col w-100 pb-24 mt-24">
				<div className="folder-items-grid">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton
							key={i}
							variant="rounded"
							height={180}
							sx={{ borderRadius: "24px" }}
							className="mb-0 w-100 min-w-350"
						/>
					))}
				</div>
			</div>
		);
	}

	if (flatItems.length === 0) {
		return (
			<div className="flex flex-col w-100 pb-24 pt-24">
				<FolderEmptyListView
					onClick={() => {
						openModal(ModalsNames.AddFolderContentModal, {
							folderId,
							folderName: folder?.name,
							initialTab: "course",
						});
					}}
				/>
			</div>
		);
	}

	return (
		<div className="flex flex-col w-100">
			<div className="folder-items-grid">
				{flatItems.map((item: any) => {
					const isSelected =
						isSelectMode &&
						selectedItems?.some(
							(i) => i.itemId === item.data?.id && i.itemType === "course",
						);

					return (
						<div
							key={item.id}
							data-test-id="folder-course-item"
							data-course-id={item.data?.id}
							onClick={
								isSelectMode
									? () => toggleSelection(item.data, "course", item.id)
									: undefined
							}
							className={cx(isSelectMode && "cursor-pointer p-relative")}
						>
							{isSelectMode && (
								<FolderSelectionCheckbox isSelected={!!isSelected} />
							)}
							<CourseView
								row={item.data}
								className={cx(
									"mb-0 w-100 min-w-350",
									isSelected && "border-color-primary",
									isSelectMode && "pointer-events-none",
								)}
							/>
						</div>
					);
				})}
				<FolderEmptyItem
					onClick={() => {
						openModal(ModalsNames.AddFolderContentModal, {
							folderId,
							folderName: folder?.name,
							initialTab: "course",
						});
					}}
				/>
			</div>
			{hasNextPage && (
				<div className="flex justify-center items-center gap-16 mt-16">
					<OutlinedPrimaryButton
						onClick={() => fetchNextPage()}
						disabled={isFetchingNextPage}
					>
						{isFetchingNextPage
							? t("folder.loading", "Ładowanie...")
							: t("folder.loadMore", "Załaduj więcej")}
					</OutlinedPrimaryButton>
					<div className="flex flex-col justify-center size-14 text-black-80">
						<span>{t("folder.showing", "Wyświetlane:")}</span>
						<span>
							{t(
								"folder.showingCountValues",
								"{{loaded}} z {{total}} elementów",
								{
									loaded: flatItems.length,
									total: totalCount,
								},
							)}
						</span>
					</div>
				</div>
			)}
		</div>
	);
};
